// ============================================================
// composerKeys - what a key does in the vox composer (ChatComposer).
//
// Esc closes the completion popup first, and with it closed hands focus
// back to the map. While the popup lists something, Up/Down move the
// highlight and Tab takes it; Enter takes a command, or a person/group
// while the line is still one or two words, and otherwise sends. With
// no popup, Enter sends. Any other key is typing.
// ============================================================

export type ComposerKeyAction = 'close' | 'leave' | 'next' | 'prev' | 'take' | 'submit';

export interface ComposerKeyState {
  /** Whether the completion popup is open (it may still list nothing). */
  open: boolean;
  /** How many completions it lists. */
  options: number;
  /** Whether the highlighted completion is a command. */
  highlightIsCommand: boolean;
  /** How many words the line holds. */
  words: number;
}

function popupAction(key: string, s: ComposerKeyState): ComposerKeyAction | null {
  if (key === 'ArrowDown') return 'next';
  if (key === 'ArrowUp') return 'prev';
  if (key === 'Tab') return 'take';
  // Past two words the line is a message, so Enter sends it rather than
  // completing the last word - unless what is highlighted is a command.
  if (key === 'Enter' && (s.highlightIsCommand || s.words <= 2)) return 'take';
  return null;
}

/** The action for `key`, or null when the key is ordinary typing. */
export function composerKeyAction(key: string, s: ComposerKeyState): ComposerKeyAction | null {
  if (key === 'Escape') return s.open ? 'close' : 'leave';
  const fromPopup = s.open && s.options > 0 ? popupAction(key, s) : null;
  if (fromPopup) return fromPopup;
  return key === 'Enter' ? 'submit' : null;
}
